import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../components/seo/SeoHead";
import SeoMarketingShell from "../../components/seo/SeoMarketingShell";

const faqItems = [
  {
    q: "Do you deliver to the door only?",
    a: "No. Bellhopt is built around in-home stocking and organization when access is provided.",
  },
  {
    q: "What is the minimum lead time?",
    a: "24 hours. This protects quality and avoids rushed, inconsistent arrivals.",
  },
  {
    q: "Is this an official Airbnb service?",
    a: "Bellhopt is an independent service designed for short-term rentals. We are not affiliated with Airbnb.",
  },
  {
    q: "How do hosts and property managers onboard?",
    a: "Either through a supported integration, or manual onboarding. Once a property is set up, we maintain up-to-date property details so our operations can dispatch and handle logistics reliably.",
  },
  {
    q: "What if items are out of stock?",
    a: "We follow substitution preferences when available. If a substitution is unclear, we prioritize guest-friendly alternatives that match the intent of the item.",
  },
  {
    q: "Can you stock alcohol?",
    a: "If alcohol is permitted, it must comply with local rules and the property's policies. Many hosts and buildings have restrictions, so we align to property instructions.",
  },
  {
    q: "Do you support multi-property portfolios?",
    a: "Yes. Bellhopt supports any size portfolio, including enterprise portfolios of 1,000+ homes.",
  },
  {
    q: "What happens if a guest changes their mind?",
    a: "If the order is updated before dispatch, we follow the updated request. If the order is already in progress, changes may be limited by timing and availability.",
  },
];

const GLASS_CARD =
  "rounded-3xl border border-[#E5E7EB] bg-white/95 backdrop-blur-2xl shadow-[0_24px_80px_rgba(15,23,42,0.12)]";
const GLASS_CARD_SOFT =
  "rounded-3xl border border-[#E5E7EB] bg-white/92 backdrop-blur-2xl shadow-[0_18px_60px_rgba(15,23,42,0.10)]";
const PRIMARY_CTA =
  "rounded-full px-7 py-3 text-white text-[12px] font-semibold tracking-[0.12em] uppercase bg-gradient-to-r from-[#FF4D4D] via-[#FF6B6B] to-[#FF4D4D] shadow-[0_14px_40px_rgba(248,113,113,0.45)] hover:shadow-[0_18px_54px_rgba(248,113,113,0.55)] transition-all";
const SECONDARY_CTA =
  "rounded-full px-7 py-3 text-[#111827] text-[12px] font-semibold tracking-[0.08em] uppercase border border-[#E5E7EB] bg-white hover:bg-[#F9FAFB] transition-all";

function Section({ id, title, children }) {
  return (
    <section id={id} className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
      <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">{title}</h2>
      <div className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">{children}</div>
    </section>
  );
}

function AirbnbGroceryStockingPage() {
  const title = "Airbnb Grocery Stocking Service | Stocked And Organized | Bellhopt";
  const description =
    "Groceries delivered, stocked, and organized before your Airbnb stay by Bellhopt Concierge. 24-hour lead time. Built for any size portfolio, including 1,000+ homes.";
  const canonical = "https://bellhopt.com/airbnb-grocery-stocking/";

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Airbnb Grocery Stocking, Stocked And Organized For A 5-Star Arrival",
      url: canonical,
      description,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.a,
        },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://bellhopt.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Airbnb Grocery Stocking",
          item: canonical,
        },
      ],
    },
  ];

  return (
    <SeoMarketingShell>
      <SeoHead
        title={title}
        description={description}
        canonical="/airbnb-grocery-stocking/"
        robots="index,follow"
        ogTitle="Airbnb Grocery Stocking, Done For You"
        ogDescription="Not just door delivery. Bellhopt Concierge stocks and organizes the home for a 5-star arrival. 24-hour lead time."
        structuredData={structuredData}
      />

      <main className="w-[90%] max-w-[1100px] m-auto pt-24 pb-10 mobile:w-[95%]">
        <section className={`${GLASS_CARD} p-8 mobile:p-5`}>
          <h1 className="font-segoe font-semibold text-[42px] mobile:text-[30px] text-[#111827] leading-tight">
            Airbnb Grocery Stocking, Stocked And Organized For A 5-Star Arrival
          </h1>
          <p className="mt-4 text-[20px] mobile:text-[17px] text-[#6B7280] leading-8">
            Bellhopt Concierge does more than deliver to the door. We deliver, stock, and organize
            the home so guests walk into a ready-to-live-in space.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>
              Get Started With Bellhopt
            </a>
            <Link to="/for/property-managers" className={SECONDARY_CTA}>
              For Property Managers
            </Link>
          </div>
          <p className="mt-4 text-[13px] text-[#6B7280]">
            24-hour lead time required. Built for any size portfolio, including 1,000+ homes.
          </p>
        </section>

        <section className={`mt-6 ${GLASS_CARD_SOFT} p-6 mobile:p-4`}>
          <h2 className="font-segoe font-semibold text-[22px] text-[#111827]">On This Page</h2>
          <div className="mt-3 flex flex-wrap gap-3">
            {[
              ["stocking", "What Stocking Means"],
              ["how-it-works", "How It Works"],
              ["portfolio", "Property Managers"],
              ["expect", "What You Can Expect"],
              ["lead-time", "24-Hour Lead Time"],
              ["why", "Why Guests Choose Stocking"],
              ["faq", "FAQ"],
            ].map(([id, label]) => (
              <a
                key={id}
                href={`#${id}`}
                className="px-4 py-2 rounded-full border border-[#E5E7EB] text-[#111827] bg-white hover:bg-[#F9FAFB] transition-all"
              >
                {label}
              </a>
            ))}
          </div>
        </section>

        <div className="mt-6 space-y-6">
          <Section id="stocking" title='What "Stocking" Means At Bellhopt'>
            <p>Most "Airbnb grocery delivery" is curb or doorstep drop-off.</p>
            <p className="mt-4">Bellhopt grocery stocking is in-home. Our Bellhopt Concierge:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>Delivers groceries to the property</li>
              <li>Stocks the fridge, pantry, and countertops where items belong</li>
              <li>Organizes for a clean, premium first impression</li>
              <li>Leaves the home arrival-ready</li>
            </ul>
            <p className="mt-4">
              If you want the home to feel prepared, not just supplied, this is the difference.
            </p>
          </Section>

          <Section id="how-it-works" title="How It Works">
            <ol className="list-decimal pl-6 space-y-4">
              <li>
                <strong>Share the stay details</strong>
                <p>
                  Hosts and property managers onboard via integration, or manually if needed. Once
                  a property is set up, we keep property details up to date so our operations can
                  dispatch and handle logistics reliably.
                </p>
              </li>
              <li>
                <strong>Guests shop ahead of time</strong>
                <p>Guests browse 20,000+ products and order exactly what they want through a pre-arrival shopping experience.</p>
              </li>
              <li>
                <strong>Bellhopt Concierge handles the run</strong>
                <p>
                  We shop, deliver, stock, and organize the home based on the order and property
                  instructions.
                </p>
              </li>
              <li>
                <strong>Arrival, upgraded</strong>
                <p>
                  Guests walk into a stocked and organized space that feels intentional and
                  high-touch.
                </p>
              </li>
            </ol>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              Make Arrival Effortless
            </h2>
            <p className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              Pre-arrival groceries, stocked and organized by Bellhopt Concierge. 24-hour lead
              time required.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>
                Get Started With Bellhopt
              </a>
              <Link to="/for/property-managers" className={SECONDARY_CTA}>
                For Property Managers
              </Link>
            </div>
          </section>

          <Section id="portfolio" title="Built For Property Managers And Enterprise Portfolios">
            <p>This is where Bellhopt wins.</p>
            <p className="mt-4">
              If you manage a portfolio, the hard part is not "delivery", it is consistency.
              Bellhopt is designed to be dispatchable, repeatable, and easy to roll out across
              markets.
            </p>
            <p className="mt-4">You get:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>Portfolio-friendly onboarding (integration or manual)</li>
              <li>Standardized arrival experience across markets</li>
              <li>A guest-facing flow that reduces back-and-forth</li>
              <li>
                Bellhopt Concierge execution so your team is not stuck coordinating runs
              </li>
            </ul>
            <div className="mt-6 rounded-2xl border border-[#E5E7EB] p-5 bg-[#F9FAFB]/90">
              <h3 className="font-segoe font-semibold text-[24px] mobile:text-[20px] text-[#111827]">
                Run This Across Your Portfolio
              </h3>
              <p className="mt-2 text-[#6B7280]">
                Launch a consistent pre-arrival grocery stocking experience across any size
                portfolio, including 1,000+ homes.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <a href="mailto:sales@bellhopt.com" className={SECONDARY_CTA}>
                  Talk To Sales
                </a>
                <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>
                  Get Started
                </a>
              </div>
            </div>
          </Section>

          <Section id="expect" title="What You Can Expect">
            <p className="font-segoe font-semibold text-[#111827]">Included:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>In-home grocery stocking (not door-only drop-off)</li>
              <li>Clean placement and basic organization for an arrival-ready look</li>
              <li>Coordination aligned to your property access method</li>
            </ul>
            <p className="font-segoe font-semibold text-[#111827] mt-5">Not included:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>Same-day orders (24-hour lead time required)</li>
              <li>Cooking or meal prep</li>
              <li>Deep cleaning services</li>
            </ul>
          </Section>

          <Section id="lead-time" title="The 24-Hour Lead Time Policy">
            <p>
              To protect the guest experience and ensure reliable execution, Bellhopt requires a
              minimum 24-hour lead time for grocery stocking orders.
            </p>
            <p className="mt-4">
              If you need exceptions for enterprise agreements, that is handled commercially with
              clear terms and rush rules.
            </p>
          </Section>

          <Section id="why" title="Why Guests Choose Stocking Over Delivery">
            <p>People do not remember "groceries arrived."</p>
            <p className="mt-2">They remember walking into a home that feels ready.</p>
            <p className="mt-4">Stocking and organization creates:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>A calmer first hour</li>
              <li>Less friction after travel</li>
              <li>A premium "this place is dialed" feeling</li>
              <li>Higher perceived quality of the stay</li>
            </ul>
          </Section>

          <Section id="faq" title="FAQ">
            <div className="space-y-3">
              {faqItems.map((item) => (
                <details key={item.q} className="rounded-2xl border border-[#E5E7EB] p-4 bg-white/90 open:bg-[#F9FAFB]">
                  <summary className="cursor-pointer font-segoe font-semibold text-[18px] text-[#111827]">
                    {item.q}
                  </summary>
                  <p className="mt-3 text-[16px] text-[#6B7280] leading-7">{item.a}</p>
                </details>
              ))}
            </div>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              Ready For A 5-Star Arrival Standard?
            </h2>
            <p className="mt-3 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              Give guests a stocked, organized home before they walk in.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>
                Get Started
              </a>
              <a href="mailto:sales@bellhopt.com" className={SECONDARY_CTA}>
                Talk To Sales
              </a>
            </div>
          </section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              Related Pages
            </h2>
            <p className="mt-3 text-[#6B7280]">
              Explore linked hubs for conversion and topical depth:
            </p>
            <ul className="mt-4 list-disc pl-6 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              <li>
                <Link className="text-primary" to="/pre-arrival-groceries">
                  Pre-arrival groceries
                </Link>
              </li>
              <li>
                <Link className="text-primary" to="/vacation-rental-grocery-delivery">
                  Vacation rental grocery delivery
                </Link>
              </li>
              <li>
                <Link className="text-primary" to="/for/property-managers">
                  For property managers
                </Link>
              </li>
              <li>
                <Link className="text-primary" to="/integrations">
                  Integrations
                </Link>
              </li>
            </ul>
          </section>
        </div>
      </main>
    </SeoMarketingShell>
  );
}

export default AirbnbGroceryStockingPage;
